/**
 * DryRunConfirmDialog (editable-workspace-processes, PR3) — the founder's
 * signature warning. EVERY consequential process/stage/template edit first
 * calls the endpoint with ?dry_run=1 and renders the REAL server-computed
 * numbers here before the commit.
 *
 * Register law (docs/reference/design-language.md §1):
 *   - caution / gold frames the CONSEQUENCE block (awaiting-your-confirm), the
 *     one register for "are you sure". NOT destructive-red decoration.
 *   - pepper ONLY on the newly-blocked line (a gate now blocks advance) when
 *     its count > 0 — the single "missing-and-it-gates-action" fact.
 *   - all counts 0 → a plain neutral (sand) "Open cards stay as they are.", no hue
 *     where there is no signal.
 *   - a server refusal (occupied stage / open cards on archive) renders its own
 *     facts in pepper and HIDES confirm (blocking), never generic copy.
 */

import { type ReactNode } from 'react';
import { Dialog } from '@/components/primitives/Dialog';
import { Button } from '@/components/primitives/Button';
import type { DryRunImpact } from '@/lib/api';

export interface DryRunConfirmDialogProps {
  title: string;
  /** A one-line description of the action, shown above the impact facts. */
  intro?: string;
  impact: DryRunImpact | null;
  confirmLabel: string;
  /** Destructive commits (remove item / deactivate stage / archive board) get
   *  a pepper confirm button. */
  destructive?: boolean;
  /** A server refusal (or a pre-checked block): renders its facts in pepper and
   *  HIDES the confirm button (the action can't proceed until it's resolved). */
  blockingMessage?: string | null;
  submitting?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

function hasAnyImpact(i: DryRunImpact): boolean {
  return i.open_cards_affected > 0 || i.items_reset > 0 || i.gates_added_blocking > 0 || i.progress_counts_changed > 0;
}

function n(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

export function DryRunConfirmDialog({
  title,
  intro,
  impact,
  confirmLabel,
  destructive = false,
  blockingMessage,
  submitting = false,
  onConfirm,
  onCancel,
}: DryRunConfirmDialogProps): ReactNode {
  const blocked = !!blockingMessage;
  const loading = impact === null && !blocked;

  return (
    <Dialog
      role="alertdialog"
      size="sm"
      title={title}
      onClose={onCancel}
      testId="dry-run-dialog"
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onCancel} data-testid="dry-run-cancel">
            {blocked ? 'Close' : 'Cancel'}
          </Button>
          {!blocked && (
            <Button
              variant={destructive ? 'danger' : 'primary'}
              size="sm"
              disabled={submitting || loading}
              onClick={onConfirm}
              data-testid="dry-run-confirm"
            >
              {submitting ? 'Working…' : confirmLabel}
            </Button>
          )}
        </>
      }
    >
      {intro && <p className="text-[13px] text-ink-soft mb-3" data-testid="dry-run-intro">{intro}</p>}

      {loading && <p className="text-[13px] text-ink-soft" data-testid="dry-run-loading">Checking the impact…</p>}

      {blocked && (
        <div className="rounded-card border border-pepper-soft bg-pepper-soft/50 px-3 py-2.5" data-testid="dry-run-blocked">
          <p className="text-[13px] text-pepper-deep font-medium">{blockingMessage}</p>
        </div>
      )}

      {!loading && !blocked && impact && (
        hasAnyImpact(impact) ? (
          // Caution / gold consequence block — "awaiting your confirm".
          <div className="rounded-card border border-warm-300 bg-gold-soft px-3 py-2.5" data-testid="dry-run-impact">
            <ul className="text-[13px] text-caution-deep space-y-1">
              <li data-testid="dry-run-cards-affected">
                This affects {n(impact.open_cards_affected, 'open card', 'open cards')}.
              </li>
              {impact.items_reset > 0 && (
                <li data-testid="dry-run-items-reset">
                  {n(impact.items_reset, 'checked-off item', 'checked-off items')} will reset (renamed steps start over).
                </li>
              )}
              {impact.progress_counts_changed > 0 && (
                <li data-testid="dry-run-progress-changed">
                  {n(impact.progress_counts_changed, 'card', 'cards')} will show new progress.
                </li>
              )}
              {/* Pepper ONLY here, and only when > 0: a gate that now blocks advance. */}
              {impact.gates_added_blocking > 0 && (
                <li className="text-pepper-deep font-medium" data-testid="dry-run-gates-blocking">
                  {n(impact.gates_added_blocking, 'card', 'cards')} won't be able to move on until their new checklist item is done.
                </li>
              )}
            </ul>
          </div>
        ) : (
          <div className="rounded-card bg-sand px-3 py-2.5" data-testid="dry-run-no-impact">
            <p className="text-[13px] text-ink-soft">Open cards stay as they are.</p>
          </div>
        )
      )}
    </Dialog>
  );
}
